import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import { Container, Grid, TextField, Typography, Button } from "@mui/material";

import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import { handlePagingAll } from "../../../components/api/APIUtilities";
import { AdminTabs } from "../Admin";
import GameSystemPicker from "../../../components/utilities/gamesystempickers/GameSystemPicker";
import ImageUploader, {
  uploadBas64ToS3
} from "../../../components/utilities/ImageUploader";
import GenericPicker from "../../../components/utilities/GenericPicker";
import { BCPFloppyDisk } from "../../../components/icons/bcp/BCPIcons";
import { postData } from "../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
const SINGLE_GAME_MANUFACTURER = getEnvironmentVariable(
  "SINGLE_GAME_MANUFACTURER"
);

export default function AdminGameSystems(props) {
  const { t } = useTranslation();
  const { activeTab, handleTabChange, authContext } = props;

  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);

  const [gameSystems, setGameSystems] = useState(null);
  const [gameSystem, setGameSystem] = useState(null);

  useEffect(() => {
    const go = async () => {
      let queryParams = {
        "expand[]": ["image", "logo", "defaultEventImage"]
      };
      if (SINGLE_GAME_MANUFACTURER) {
        queryParams.manufacturer = SINGLE_GAME_MANUFACTURER;
      }

      var apiGameSystems = await handlePagingAll("/gamesystems", queryParams);
      if (
        !authContext?.authState?.identity?.isAdmin &&
        authContext?.authState?.identity?.adminGameSystems?.length > 0
      ) {
        apiGameSystems = apiGameSystems.filter((el) =>
          authContext.authState.identity.adminGameSystems.includes(el.id)
        );
      }

      if (apiGameSystems?.length > 0) {
        apiGameSystems.sort((a, b) => {
          if (a.manufacturerOrderNum === b.manufacturerOrderNum) {
            return a.gameSystemOrderNum - b.gameSystemOrderNum;
          } else {
            return a.manufacturerOrderNum - b.manufacturerOrderNum;
          }
        });
      }
      setGameSystems(apiGameSystems);
      setLoading(false);
    };
    go(); // eslint-disable-next-line
  }, []);

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        {/* Header Section */}
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 1,
            backgroundColor: "background.secondary"
          }}
        >
          <Typography variant="h3">{t("pages.admin.adminSettings")}</Typography>

          <AdminTabs
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </Grid>

        {/* If gamesystems is truthy then return the gameSystemPicker */}
        {gameSystems && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary"
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <GameSystemPicker
                  name="gameType"
                  value={gameSystem?.code || ""}
                  handleChange={(e) => {
                    if (e.target.value !== undefined) {
                      gameSystems.forEach((gs) => {
                        if (gs.code === e.target.value) {
                          setGameSystem(gs);
                          return true;
                        }
                      });
                    }
                  }}
                  gameSystems={gameSystems}
                />
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Loads the formik component once a gameSystem has been selected, using the values from that specific gamesystem */}
        {gameSystem && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary"
            }}
          >
            <Formik
              initialValues={{
                name: gameSystem?.name || "",
                code: gameSystem?.code || "",
                gameSystemOrderNum: gameSystem?.gameSystemOrderNum || "",
                hasFactions: gameSystem?.hasFactions || false,
                factionLabel: gameSystem?.factionLabel || "",
                hasSubFactions: gameSystem?.hasSubFactions || false,
                subFactionRequired: gameSystem?.subFactionRequired || false,
                subFactionLabel: gameSystem?.subFactionLabel || "",
                manufacturer: gameSystem?.manufacturer || "",
                manufacturerOrderNum: gameSystem?.manufacturerOrderNum || "",
                logoId: gameSystem?.logoId || "",
                imageId: gameSystem?.imageId || "",
                defaultEventImageId: gameSystem?.defaultEventImageId || ""
              }}
              validationSchema={Yup.object().shape({
                name: Yup.string().required(),
                code: Yup.number().required(),
                gameSystemOrderNum: Yup.number().required(),
                hasFactions: Yup.boolean().required(),
                factionLabel: Yup.string(),
                hasSubFactions: Yup.boolean().required(),
                subFactionRequired: Yup.boolean().required(),
                subFactionLabel: Yup.string(),
                manufacturer: Yup.string().required(),
                manufacturerOrderNum: Yup.number().required()
              })}
              enableReinitialize
              onSubmit={async (values) => {
                try {
                  setProcessing(true);

                  let sanitizedValues = {
                    ...values
                  };

                  // Handles images
                  if (sanitizedValues.image) {
                    // upload image to s3
                    let newFile = await uploadBas64ToS3(sanitizedValues.image);
                    delete sanitizedValues.image;
                    if (newFile.fileId) {
                      sanitizedValues.imageId = newFile.fileId;
                    }
                  }

                  if (sanitizedValues.logo) {
                    // upload image to s3
                    let newFile = await uploadBas64ToS3(sanitizedValues.logo);
                    delete sanitizedValues.logo;
                    if (newFile.fileId) {
                      sanitizedValues.logoId = newFile.fileId;
                    }
                  }

                  if (sanitizedValues.defaultEventImage) {
                    // upload image to s3
                    let newFile = await uploadBas64ToS3(
                      sanitizedValues.defaultEventImage
                    );
                    delete sanitizedValues.defaultEventImage;
                    if (newFile.fileId) {
                      sanitizedValues.defaultEventImageId = newFile.fileId;
                    }
                  }

                  await postData(`/gamesystems/${gameSystem.id}`, {
                    body: { set: sanitizedValues }
                  });
                  postNotification(
                    "success",
                    t("notifications.success.gameSystemUpdated")
                  );
                } catch (err) {
                  postNotification("error", err);
                }
                setProcessing(false);
              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                touched,
                values,
                setFieldValue
              }) => (
                <>
                  <form onSubmit={handleSubmit}>
                    <Grid container gap={1}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Name"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched.name && errors.name)}
                          helperText={Boolean(touched.name && errors.name)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Code"
                          name="code"
                          type="number"
                          value={values.code}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched.code && errors.code)}
                          helperText={Boolean(touched.code && errors.code)}
                          disabled={!authContext?.authState?.identity?.isAdmin}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Order Number"
                          name="manufacturerOrderNum"
                          type="number"
                          value={values.manufacturerOrderNum}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched.manufacturerOrderNum &&
                              errors.manufacturerOrderNum
                          )}
                          helperText={Boolean(
                            touched.manufacturerOrderNum &&
                              errors.manufacturerOrderNum
                          )}
                          disabled={!authContext?.authState?.identity?.isAdmin}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <GenericPicker
                          label="Has Factions?"
                          name="hasFactions"
                          value={values.hasFactions}
                          list={[
                            { label: "Yes", value: true },
                            { label: "No", value: false }
                          ]}
                          inclAny={false}
                          handleChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched.hasFactions && errors.hasFactions
                          )}
                          helperText={Boolean(
                            touched.hasFactions && errors.hasFactions
                          )}
                        />
                      </Grid>

                      {/* Dynamically generate a new input for Faction Label if this is set to true. This can likely be done with a conditional statement set to something like {values.hasFactions ? <></> : null} */}
                      {values.hasFactions && (
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            label="Faction Label"
                            name="factionLabel"
                            value={values.factionLabel}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(
                              touched.factionLabel && errors.factionLabel
                            )}
                            helperText={Boolean(
                              touched.factionLabel && errors.factionLabel
                            )}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <GenericPicker
                          label="Has Sub Factions?"
                          name="hasSubFactions"
                          list={[
                            { label: "Yes", value: true },
                            { label: "No", value: false }
                          ]}
                          inclAny={false}
                          value={values.hasSubFactions}
                          handleChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched.hasSubFactions && errors.hasSubFactions
                          )}
                          helperText={Boolean(
                            touched.hasSubFactions && errors.hasSubFactions
                          )}
                        />
                      </Grid>

                      {/* Dynamically generate a new input for subFaction Label if this is set to true. This can likely be done with a conditional statement set to something like {values.hasSubFactions ? <></> : null} */}
                      {values.hasSubFactions && (
                        <>
                          <Grid item xs={12}>
                            <GenericPicker
                              label="Sub Faction Required?"
                              name="subFactionRequired"
                              list={[
                                { label: "Yes", value: true },
                                { label: "No", value: false }
                              ]}
                              inclAny={false}
                              value={values.subFactionRequired}
                              handleChange={handleChange}
                              onBlur={handleBlur}
                              error={Boolean(
                                touched.subFactionRequired &&
                                  errors.subFactionRequired
                              )}
                              helperText={Boolean(
                                touched.subFactionRequired &&
                                  errors.subFactionRequired
                              )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              label="Sub Faction Label"
                              name="subFactionLabel"
                              value={values.subFactionLabel}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={Boolean(
                                touched.subFactionLabel &&
                                  errors.subFactionLabel
                              )}
                              helperText={Boolean(
                                touched.subFactionLabel &&
                                  errors.subFactionLabel
                              )}
                            />
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Manufacturer"
                          name="manufacturer"
                          value={values.manufacturer}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched.manufacturer && errors.manufacturer
                          )}
                          helperText={Boolean(
                            touched.manufacturer && errors.manufacturer
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Manufacturer Order Number"
                          name="manufacturerOrderNum"
                          type="number"
                          value={values.manufacturerOrderNum}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched.manufacturerOrderNum &&
                              errors.manufacturerOrderNum
                          )}
                          helperText={Boolean(
                            touched.manufacturerOrderNum &&
                              errors.manufacturerOrderNum
                          )}
                          disabled={!authContext?.authState?.identity?.isAdmin}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">Logo Image</Typography>
                        <ImageUploader
                          values={values}
                          label="Logo Image"
                          name="logo"
                          defaultUrl={
                            gameSystem?.logo?.cloudFrontDomain
                              ? `${gameSystem?.logo?.cloudFrontDomain}${gameSystem?.logo?.filename}`
                              : null
                          }
                          setFieldValue={setFieldValue}
                          noAspect
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">Banner Image</Typography>
                        <ImageUploader
                          values={values}
                          label="Banner Image"
                          name="image"
                          defaultUrl={
                            gameSystem?.image?.cloudFrontDomain
                              ? `${gameSystem?.image?.cloudFrontDomain}${gameSystem?.image?.filename}`
                              : null
                          }
                          setFieldValue={setFieldValue}
                          aspectW={getEnvironmentVariable("GAMESYSTEM_IMAGE_W")}
                          aspectY={getEnvironmentVariable("GAMESYSTEM_IMAGE_H")}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1">
                          Default Event Image
                        </Typography>
                        <ImageUploader
                          values={values}
                          label="Default Event Image"
                          name="defaultEventImage"
                          defaultUrl={
                            gameSystem?.defaultEventImage?.cloudFrontDomain
                              ? `${gameSystem?.defaultEventImage?.cloudFrontDomain}${gameSystem?.defaultEventImage?.filename}`
                              : null
                          }
                          setFieldValue={setFieldValue}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          startIcon={<BCPFloppyDisk />}
                        >
                          Save
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </>
              )}
            </Formik>
          </Grid>
        )}

        {Boolean(processing || loading) && <ModalLoadingFull />}
      </Grid>
    </Container>
  );
}
